import type { BrandAlertPopup } from './popup';

/**
 * Brand alert (SideGuide) seams.
 *
 * The client is surface- and platform-agnostic: everything it needs from the
 * outside world arrives as one of the collaborators below.
 */

/**
 * Page/platform wiring, injected by `./config`.
 */
export interface BrandAlertsConfig {
    creativeUrl: string;
    trackingUrl: string;
    /** For BVA the chat session's uuid arrives through events. */
    viewedUuid: string | null;
    /** Web: fallback used when the CSRF cookie is missing. */
    csrftoken?: string;
    /** iOS: bearer token. */
    accessToken?: string;
    /** Staff preview: signed creative id, forced past targeting. */
    forceCreativeId?: string;
}

export interface BrandAlertCreativeData {
    header: string;
    headline: string;
    expanded: string;
    indications: string;
    indications_title: string;
    isi: string;
    isi_title: string;
    show_indications: boolean;
    show_isi: boolean;
    double_isi: boolean;
    wysiwyg_editor: boolean;
    partial_indication_enabled: boolean;
    partial_indication_height: number;
    expand_time_seconds: number;
    dismiss_time_seconds: number;
    /** Seconds of Video Assistant inactivity before the BVA surface expands. */
    bva_expand_time_seconds: number;
    brand_alert_viewed_id: number;
    brand_alert_placement_id: number | null;
}

/**
 * The subset of the Brightcove player wrapper the brand alert code uses.
 */
export interface BrandAlertsPlayer {
    paused(): boolean;
    ended(): boolean;
    currentTime(): number;
    isFullscreen(): boolean;
    on(event: string, callback: () => void): void;
    one(event: string, callback: () => void): void;
}

/**
 * Mirrors `brand_alerts.constants.BrandAlertSurface`. The serving endpoint
 * requires it and 400s on an unknown value, so these strings must match the
 * backend exactly.
 */
export const SURFACE = {
    VIDEO_PLAYER_PAGE: 'video_player_page',
    BVA: 'bva',
} as const;

export type BrandAlertSurface = (typeof SURFACE)[keyof typeof SURFACE];

export const DISMISS_TYPE = {
    AUTO_DISMISS: 'auto_dismiss',
    MANUAL_DISMISS: 'manual_dismiss',
    PAGE_CLOSE_DISMISS: 'page_close_dismiss',
    CHATBOT_ACTIVE_DISMISS: 'chatbot_active_dismiss',
} as const; // these should be in sync with the values in BrandAlertViewed.Dismiss class

export type DismissType = (typeof DISMISS_TYPE)[keyof typeof DISMISS_TYPE];

export const STATE = {
    START: 'start',
    HALF_EXPANDED: 'half-expanded',
    FULL_EXPANDED: 'full-expanded',
    CLOSED: 'closed',
} as const;

export type PopupState = (typeof STATE)[keyof typeof STATE];

export type ScrollPosition = {
    scrollHeight: number;
    scrollTop: number;
    clientHeight: number;
};

export type HoverData = {
    duration: number;
    maxY: number | null;
};

export interface TrackEventData {
    eventName: string;
    data?: Record<string, unknown>;
}

export interface TrackPayload extends TrackEventData {
    brandAlertViewedId: number;
    /** GAM creative id, when the sideguide's banner filled. */
    bannerCreative?: string;
}

/**
 * Creative fetch + tracking POST. Owns the endpoints and auth — the things that
 * differ between web and iOS.
 */
export interface Transport {
    /**
     * Initial load. Honours the staff-preview override when the page carries one.
     * Rejects when the endpoint has no creative to serve (a 404).
     */
    fetchCreative(): Promise<BrandAlertCreativeData>;
    /**
     * Rotation. Deliberately does *not* re-apply the forced creative id: a
     * preview would otherwise be served the same creative forever, so the
     * sideguide would rotate in place instead of auto-dismissing when the
     * campaign is exhausted. `BrandAlertDisplayTestCase` pins that behaviour.
     */
    fetchNextCreative(): Promise<BrandAlertCreativeData>;
    track(payload: TrackPayload): void;
}

/**
 * Where the popup lives and how it moves. Every page-specific selector and all
 * layout maths lives behind this.
 */
export interface Host {
    /** Reveal the frame. Sizing is a separate `setPopupHeight()` pass the client
     * runs first — iOS depends on that pass and it must not live in one host. */
    reveal(): void;
    /** Re-run the platform's sizing pass (resize, and after a rotation swap). */
    setPopupHeight(popup: HTMLElement, secondaryWrap: HTMLElement): void;
    /** Platform-specific expand, run before the popup applies its own classes. */
    expand(popupContainer: HTMLElement): void;
    close(popup: HTMLElement, popupContainer: HTMLElement): void;
    /**
     * Return the shared shell to the state the Django include ships, so a
     * replacement client starts from a clean slate.
     *
     * Every class and inline style written during a display outlives the client
     * that wrote it — and `.is-closed` carries `transform: translateY(110%)
     * !important` (`scss/brand-alerts.scss:133`), which beats the inline
     * `translateY(0)` a new display writes. Without this, dismiss → destroy →
     * reuse renders the next sideguide permanently off-screen.
     */
    reset(): void;
    destroy(): void;
}

/**
 * Decides when to expand.
 *
 * Two steps on purpose: `start()` begins watching for playback immediately, but
 * the schedule can only be armed once the creative has arrived with its
 * `expand_time_seconds`. Arming in `start()` would let a show fire with a zero
 * delay if playback began before the fetch resolved.
 */
export interface ShowTrigger {
    /**
     * Fires when the creative may be fetched.
     */
    onArm(callback: () => void): void;
    /** Begin watching. Called before the creative is fetched. */
    start(): void;
    /**
     * The creative arrived: arm the schedule from it.
     */
    scheduleShow(creative: BrandAlertCreativeData): void;
    /** Fires when ready, including after renewed activity delays a pending display. */
    onReady(callback: () => void): void;
    /** Rechecked on postponed display attempts; activity can revoke readiness. */
    isReady(): boolean;
    /** Stop when the display is shown, permanently blocked, or the client is destroyed. */
    destroy(): void;
}

export type BlockerVerdict =
    | { kind: 'clear' }
    /** Bail once and track `reason`. */
    | { kind: 'block'; reason: string | null }
    /** Retry on the next tick. */
    | { kind: 'postpone' };

export interface Blocker {
    /** `block` bails and consumes the display; `postpone` retries. */
    kind: 'block' | 'postpone';
    /** True while this blocker is active. */
    blocked(): boolean;
    /** Analytics event name to track when a `block` bails. */
    reason?(): string | null;
    destroy?(): void;
}

/** The half of {@link Blocker} the client drives — the page's `setBlocked()`. */
export interface ManualBlock {
    set(blocked: boolean, reason: string): void;
}

export interface Blockers {
    evaluate(): BlockerVerdict;
    destroy(): void;
}

/**
 * What the popup needs from its owner.
 *
 * Every callback carries the popup that raised it. During a rotation two popups
 * share one delegate and the *incoming* one is the visible, clickable one — a
 * delegate that resolved "the active alert" instead would act on the wrong popup
 * and leave the other looking dismissable.
 */
export interface PopupDelegate {
    track(payload: TrackPayload): void;
    /** Platform expand, run before the popup applies its own classes. */
    beforeExpand(popup: BrandAlertPopup): void;
    /** `dismiss_time_seconds` elapsed with no interaction. */
    onDismissTimerElapsed(popup: BrandAlertPopup): void;
    onDismissed(popup: BrandAlertPopup, type: DismissType): void;
}
